/**
 * Tiny colour helpers for merchant palettes (hex in, hex out). Merchant
 * colours arrive from a logo measurement, so every text-on-colour pairing
 * is decided by contrast here, never assumed.
 */

type Rgb = [number, number, number];

export const hexToRgb = (hex: string): Rgb => {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as Rgb;
};

const rgbToHex = (rgb: Rgb) =>
  "#" +
  rgb
    .map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16))
    .map((h) => (h.length < 2 ? "0" + h : h))
    .join("");

/** WCAG relative luminance, 0 (black) → 1 (white). */
export const luminance = (hex: string) => {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two colours (1 → 21). */
export const contrast = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** Linear mix: `t` = 0 → a, 1 → b. */
export const mix = (a: string, b: string, t: number) => {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex(ca.map((v, i) => v + (cb[i] - v) * t) as Rgb);
};

/** `rgba()` from a hex, for shadows and scrims. */
export const alpha = (hex: string, a: number) => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/** Of `candidates`, the one that reads best on `bg`. */
export const bestOn = (bg: string, candidates: string[]) =>
  candidates.reduce((best, c) =>
    contrast(c, bg) > contrast(best, bg) ? c : best,
  );

/** Near-white (a logo on a white tile has no usable tile colour). */
export const isPale = (hex: string) => luminance(hex) > 0.8;
